import { ChangeEvent, SetStateAction } from 'react'
import { sub } from 'date-fns'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { GameGroups } from 'constants/ticket'
import { TicketHistoryType } from 'components/cashier-ticket-history'
import {
  ClientMenuOptions,
  CombiBetDraw,
  CombiBetTicket,
  CombiCashierTicket,
  FreeTicket,
  SelectedBall,
  SelectedDraw,
  TicketState,
} from 'shared-types'

export type Ticket = {
  selectedBalls: SelectedBall[]
  confirmed: boolean
  drawTypeKey?: string
  drawOptionKey?: string
  gameGroupId: number
  stake?: number
  quantityOfNumbers?: number
  wasPermutationAccepted: boolean
  fixedStake?: number
}
export type Draws = {
  [drawGroupId in number | string]: Date[]
}
type TicketStateUpdater = SetStateAction<TicketState[]> | ((prevState: TicketState[]) => TicketState[])
export const hoursFilterOptions = [1, 2, 24] as const
export type HoursFilter = (typeof hoursFilterOptions)[number]

export type TicketAggr = {
  draws: Draws
  tickets: Ticket[]
  code?: string
  gameGroupId: GameGroups
  prefilledForCashier: boolean
  isJackpot: boolean
  availableDrawsForBrandId?: number[]
}

export const createTicketsDefaultState = (isJackpot: boolean, gameGroup: number): TicketState => ({
  drawTypeId: null,
  drawWonTypeId: null,
  drawsIds: [],
  maxStake: null,
  minStake: null,
  potentialWinning: null,
  stake: null,
  isAddedToTicket: false,
  oddsBoosted: null,
  code: null,
  isCashierTicket: false,
  selectedGames: {
    category: null,
    subCategory: null,
    numbers: null,
    pickedBalls: [],
    isPermutationAccepted: false,
    isJackpot: isJackpot,
    isExclusive: gameGroup === 1,
  },
})
type GlobalStateValues = {
  gameGroupId: GameGroups
  hoursFilter: HoursFilter
  countriesFilter: number[]
  jackpotPoolValue: string | boolean
  cashierSearchedBookingCode: string
  selectedLanguage?: string
  ticketHistoryFrom?: Date
  ticketHistoryTo?: Date
  ticketHistoryType?: TicketHistoryType
  isPremiumMode: boolean
  clientMenuOption?: ClientMenuOptions
  midContainerRef: null | HTMLDivElement
  isSystemBetToggled?: boolean
  isCombiBetToggled: boolean
  freeTicket: FreeTicket
  isWithdrawalError: boolean
  isWithdrawalSuccess: boolean
  isPaymentCloseButtonClicked: boolean
  isLoggedIn: boolean
  selectedCombiBetDraw: CombiBetDraw | null
  selectedTypeOfTicket: string
  selectedDraws: SelectedDraw[]
  ticketsState: TicketState[]
  jackpotTicketState: TicketState[]
  exclusiveTicketState: TicketState[]
  selectedTabIndex: number
  cashierTicket: TicketState[]
  cashierDraws: SelectedDraw[]
  fromBookingCodeSite: boolean
  isDepositWithdrawalOpen: boolean
  combiBetTickets: CombiBetTicket[]
  cashierCombiTicket: CombiCashierTicket
  triggerSessionEndedModal: number
}
type GlobalStateActions = {
  setGameGroupId: (val: GameGroups) => void
  setHoursFilter: (val: HoursFilter) => void
  setCountriesFilter: (val: number[]) => void
  setJackpotPoolValue: (val: string | boolean) => void
  setCashierSearchedBookingCode: (val: string) => void
  setSelectedLanguage: (val: string) => void
  setTicketHistoryFrom: (val: Date | undefined, e?: ChangeEvent<HTMLInputElement>) => void
  setTicketHistoryTo: (val: Date | undefined, e?: ChangeEvent<HTMLInputElement>) => void
  setTicketHistoryType: (val: TicketHistoryType) => void
  setIsPremiumMode: (val: boolean) => void
  setClientMenuOption: (val?: ClientMenuOptions) => void
  setMidContainerRef: (val: HTMLDivElement | null) => void
  setIsSystemBetToggled: (val?: boolean) => void
  setIsCombiBetToggled: (val: boolean) => void
  setIsWithdrawalError: (val: boolean) => void
  setIsWithdrawalSuccess: (val: boolean) => void
  setIsPaymentCloseButtonClicked: (val: boolean) => void
  setIsLoggedIn: (val: boolean) => void
  setSelectedCombiBetDraw: (val: CombiBetDraw | null) => void
  setCombiBetTickets: (updater: SetStateAction<CombiBetTicket[]>) => void
  setSelectedDraws: (updater: SetStateAction<SelectedDraw[]>) => void
  setTicketsState: (updater: SetStateAction<TicketState[]>) => void
  setJackpotTicketState: (u: TicketStateUpdater) => void
  setExclusiveTicketState: (u: TicketStateUpdater) => void
  setSelectedTypeOfTicket: (val: string) => void
  setSelectedTabIndex: (val: number) => void
  setCashierTicket: (updater: TicketStateUpdater) => void
  setCashierDraws: (val: SelectedDraw[]) => void
  setFromBookingCodeSite: (val: boolean) => void
  setIsDepositWithdrawalOpen: (val: boolean) => void
  setCashierCombiTicket: (updater: Partial<CombiCashierTicket> | ((draft: CombiCashierTicket) => void)) => void
  setFreeTicket: (val: FreeTicket) => void
  setTriggerSessionEndedModal: (val: number) => void
}
type GlobalState = GlobalStateValues & GlobalStateActions

export const useGlobalStore = create<GlobalState, [['zustand/immer', never]]>(
  immer((set) => ({
    freeTicket: {},
    gameGroupId: GameGroups.lotto,
    hoursFilter: 24,
    countriesFilter: [],
    jackpotPoolValue: false,
    cashierSearchedBookingCode: '',
    selectedLanguage: window.localStorage.getItem('lotto-selected-language') || undefined,
    ticketHistoryFrom: sub(new Date(), { hours: 1 }),
    ticketHistoryTo: new Date(),
    ticketHistoryType: 'regular',
    isPremiumMode: false,
    clientMenuOption: undefined,
    midContainerRef: null,
    isSystemBetToggled: undefined,
    isCombiBetToggled: false,
    isWithdrawalError: false,
    isWithdrawalSuccess: false,
    isPaymentCloseButtonClicked: false,
    isLoggedIn: false,
    selectedCombiBetDraw: null,
    combiBetTickets: [],
    selectedDraws: [],
    ticketsState: [createTicketsDefaultState(false, 0)],
    jackpotTicketState: [createTicketsDefaultState(true, 0)],
    exclusiveTicketState: [createTicketsDefaultState(false, 1)],
    selectedTypeOfTicket: '',
    selectedTabIndex: 0,
    cashierTicket: [],
    cashierDraws: [],
    fromBookingCodeSite: false,
    isDepositWithdrawalOpen: false,
    cashierCombiTicket: {
      currency: null,
      outcomes: [],
      slipCode: null,
      stake: null,
      tax: null,
      totalOdds: null,
      winning: null,
    },
    triggerSessionEndedModal: 0,

    // --- Setters (mutators) ---
    setGameGroupId: (val) => set({ gameGroupId: val }),
    setHoursFilter: (val) => set({ hoursFilter: val }),
    setCountriesFilter: (val) => set({ countriesFilter: val }),
    setJackpotPoolValue: (val) => set({ jackpotPoolValue: val }),
    setCashierSearchedBookingCode: (val) => set({ cashierSearchedBookingCode: val }),
    setSelectedLanguage: (val) =>
      set(() => {
        window.localStorage.setItem('lotto-selected-language', val)
        return { selectedLanguage: val }
      }),
    setTicketHistoryFrom: (val) => set({ ticketHistoryFrom: val }),
    setTicketHistoryTo: (val) => set({ ticketHistoryTo: val }),
    setTicketHistoryType: (val) => set({ ticketHistoryType: val }),
    setIsPremiumMode: (val) => set({ isPremiumMode: val }),
    setClientMenuOption: (val) => set({ clientMenuOption: val }),
    setMidContainerRef: (ref) => set({ midContainerRef: ref }),
    setIsSystemBetToggled: (val) => set({ isSystemBetToggled: val }),
    setIsCombiBetToggled: (val) => set({ isCombiBetToggled: val }),
    setIsWithdrawalError: (val) => set({ isWithdrawalError: val }),
    setIsWithdrawalSuccess: (val) => set({ isWithdrawalSuccess: val }),
    setIsPaymentCloseButtonClicked: (val) => set({ isPaymentCloseButtonClicked: val }),
    setIsLoggedIn: (val) => set({ isLoggedIn: val }),
    setSelectedCombiBetDraw: (val) => set({ selectedCombiBetDraw: val }),
    setCombiBetTickets: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            combiBetTickets: update(state.combiBetTickets),
          }
        }
        return {
          combiBetTickets: update,
        }
      }),
    setSelectedDraws: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            selectedDraws: update(state.selectedDraws),
          }
        }

        return {
          selectedDraws: update,
        }
      }),
    setTicketsState: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            ticketsState: update(state.ticketsState),
          }
        }

        return {
          ticketsState: update,
        }
      }),
    setJackpotTicketState: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            jackpotTicketState: update(state.jackpotTicketState),
          }
        }

        return {
          jackpotTicketState: update,
        }
      }),
    setExclusiveTicketState: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            exclusiveTicketState: update(state.exclusiveTicketState),
          }
        }

        return {
          exclusiveTicketState: update,
        }
      }),
    setSelectedTypeOfTicket: (val) => set({ selectedTypeOfTicket: val }),
    setSelectedTabIndex: (val) => set({ selectedTabIndex: val }),
    setCashierTicket: (update) =>
      set((state) => {
        if (typeof update === 'function') {
          return {
            cashierTicket: update(state.cashierTicket),
          }
        }

        return {
          cashierTicket: update,
        }
      }),
    setCashierDraws: (val) => set({ cashierDraws: val }),
    setFromBookingCodeSite: (val) => set({ fromBookingCodeSite: val }),
    setIsDepositWithdrawalOpen: (val) => set({ isDepositWithdrawalOpen: val }),
    setCashierCombiTicket: (updater) =>
      set((state) => {
        if (typeof updater === 'function') {
          return {
            cashierCombiTicket: updater(state.cashierCombiTicket),
          }
        }
        return {
          cashierCombiTicket: {
            ...state.cashierCombiTicket,
            ...updater,
          },
        }
      }),
    setFreeTicket: (val) => set({ freeTicket: val }),
    setTriggerSessionEndedModal: (val) => set({ triggerSessionEndedModal: val }),
  }))
)
